import * as React from "react";
import TaskList from "src/components/TaskListPanel/TaskList";
import TaskUploader from "src/components/TaskUploader/TaskUploader";
import {
  AddNewTaskWrapper,
  CSVFileInput,
  ItemLabel,
  TaskAddIcon,
  TaskItemWrapper,
  TaskLabel,
  TaskListGorrionIcon,
  TaskListPanelWrapper,
  TaskPanelContentWrapper,
  TaskPanelWrapper,
  UpperRowWrapper,
  TaskIcon,
} from "src/components/TaskListPanel/TaskListPanel.components";
import TaskListTitle from "src/components/TaskListPanel/TaskListTitle/TaskListTitle";
import { useGameContext } from "src/game/useGameContext";
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { displayErrorToast, displaySuccessToast } from "../shared/Toasts/Toast";
import { IMPORT_TASKS_CSV } from "src/utils/restEndpoints";
import { UserRole } from "src/enums/user/userRoles";

const TaskListPanel: React.FunctionComponent = () => {
  const { gameState } = useGameContext();
  const queryClient = useQueryClient();

  const endListRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const importTaskList = async (selectedFile: File) => {
    if (!selectedFile) {
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);

    const { data: response } = await axios.post(
      `${process.env.REACT_APP_SOCKET_URL}${IMPORT_TASKS_CSV}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${gameState.token ?? ""}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  };

  const { mutate } = useMutation(importTaskList, {
    onSuccess: () => {
      displaySuccessToast("Successfully imported tasks");
      setIsAddingNewTask(false);
    },
    onError: () => {
      displayErrorToast("Failed to import tasks");
    },
    onSettled: () => {
      queryClient.invalidateQueries("create");
    },
  });

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.length ? e.target.files[0] : null;
    if (file) {
      mutate(file);
      e.target.value = "";
      e.target.files = null;
    }
  };

  const [taskListOpen, setTaskListOpen] = React.useState(false);
  const [isAddingNewTask, setIsAddingNewTask] = React.useState(false);

  React.useEffect(() => {
    if (isAddingNewTask) {
      endListRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isAddingNewTask]);

  const hasTasks = () => {
    if (!gameState.game?.allTasks) {
      return false;
    }

    return gameState.game?.allTasks?.length < 1;
  };

  return (
    <TaskPanelWrapper>
      <TaskItemWrapper
        onClick={() => {
          setTaskListOpen(true);
          setIsAddingNewTask(false);
        }}>
        <TaskIcon />
        <ItemLabel>Tasks</ItemLabel>
      </TaskItemWrapper>
      <TaskListPanelWrapper data-testid="tasksList" isVisible={taskListOpen}>
        <TaskListTitle closeTaskList={() => setTaskListOpen(false)} />
        {gameState.user?.role == UserRole.OWNER && (
          <UpperRowWrapper>
            <AddNewTaskWrapper
              onClick={() => {
                setIsAddingNewTask(true);
              }}>
              <TaskAddIcon />
              <TaskLabel>Add new task</TaskLabel>
            </AddNewTaskWrapper>
            <CSVFileInput
              type="file"
              ref={fileInputRef}
              onChange={handleChangeFile}
            />
          </UpperRowWrapper>
        )}
        <TaskPanelContentWrapper>
          {hasTasks() &&
          !isAddingNewTask &&
          gameState.user?.role == UserRole.OWNER ? (
            <TaskUploader
              onSelectionChange={droppedFile => {
                mutate(droppedFile);
              }}
              onHandleChange={handleChangeFile}
            />
          ) : (
            <TaskList
              onToggleNewTaskPanel={() => setIsAddingNewTask(!isAddingNewTask)}
              isAddingTask={isAddingNewTask}
              taskListEndRef={endListRef}
            />
          )}
        </TaskPanelContentWrapper>
        <TaskListGorrionIcon data-testid="gorrionIcon" />
      </TaskListPanelWrapper>
    </TaskPanelWrapper>
  );
};

export default TaskListPanel;
